import React, { useEffect } from 'react'
import { Button, SectionCard } from '@enamelai/design-system'
import { Icon as ArrowRightIcon } from '@enamelai/design-system/icons/Arrow_2_right'
import { Icon as BillingIcon } from '@enamelai/design-system/icons/Billing'
import { Icon as WalletIcon } from '@enamelai/design-system/icons/Wallet'
import ConfirmDeleteModal from '@components/layout/Modals/ConfirmDeleteModal/ConfirmDeleteModal.component'
import ArSummaryCards from '@components/common/ArSummary/ArSummaryCards.component'
import { formatNumber } from '@utils/numberFormatter.util'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import PatientProfilePaymentMethodItemCard from '@pages/PatientProfile/shared/PatientProfilePaymentMethodItemCard.component'
import PatientProfileSavedCardItemCard from '@pages/PatientProfile/shared/PatientProfileSavedCardItemCard.component'
import ProfileEmptyState from '@pages/PatientProfile/shared/ProfileEmptyState.component'
import usePatientProfileAccountSummary from './usePatientProfileAccountSummary'
import './PatientProfileAccountSummary.css'

const ACCOUNT_SUMMARY_LOADING_CONTAINER = '.patient-profile-account'

type PatientProfileAccountSummaryProps = {
  useDemoData?: boolean
  forceEmptyStates?: boolean
}

function PatientProfileAccountSummary({
  useDemoData = false,
  forceEmptyStates = false,
}: PatientProfileAccountSummaryProps): React.ReactElement {
  const {
    data,
    paymentMethods,
    savedCards,
    patientARAnalytics,
    insuranceARAnalytics,
    agingPeriods,
    arAgingTotal,
    isLoading,
    balanceLeftRows,
    balanceRightRows,
    showDeleteModal,
    deleteModalTitle,
    deleteModalBody,
    handleRequestDeletePaymentMethod,
    handleRequestDeleteSavedCard,
    handleCloseDeleteModal,
    handleConfirmDelete,
    isDeleting,
    isSettingDefault,
    isPaymentNetworkBusy,
    handleSetDefaultPaymentMethod,
    handleSetDefaultSavedCard,
    navigateToLedger,
  } = usePatientProfileAccountSummary({ useDemoData })
  const { showLoading, hideLoading } = useGlobalLoadingActions()

  useEffect(() => {
    if (isPaymentNetworkBusy) {
      showLoading({ container: ACCOUNT_SUMMARY_LOADING_CONTAINER })
    } else {
      hideLoading(ACCOUNT_SUMMARY_LOADING_CONTAINER)
    }
    return () => hideLoading(ACCOUNT_SUMMARY_LOADING_CONTAINER)
  }, [isPaymentNetworkBusy, showLoading, hideLoading])

  return (
    <div className="patient-profile-account">
      <ArSummaryCards
        patientAR={patientARAnalytics}
        insuranceAR={insuranceARAnalytics}
        agingPeriods={agingPeriods}
        arAgingTotal={arAgingTotal}
      />

      <SectionCard
        title="Account summary"
        className="patient-profile-section"
        headerRight={
          <Button
            variant="primary"
            size="md"
            rightIcon={
              <ArrowRightIcon aria-hidden="true" width={16} height={16} />
            }
            onClick={navigateToLedger}
          >
            View in ledger
          </Button>
        }
      >
        <div className="patient-profile-account-summary-panel">
          <div className="patient-profile-account-outstanding-row">
            <span>Outstanding balance:</span>
            <span>
              {isLoading ? '--' : formatNumber(data.balances.outstanding, true)}
            </span>
          </div>
          <div className="patient-profile-account-balance-columns">
            <div className="patient-profile-account-balance-col">
              {balanceLeftRows.map((row) => (
                <div
                  key={row.label}
                  className={`patient-profile-account-balance-line${
                    row.medium
                      ? ' patient-profile-account-balance-line--medium'
                      : ''
                  }`}
                >
                  <span>{row.label}</span>
                  <span>{row.value}</span>
                </div>
              ))}
            </div>
            <div className="patient-profile-account-balance-col">
              {balanceRightRows.map((row) => (
                <div
                  key={row.label}
                  className="patient-profile-account-balance-line"
                >
                  <span>{row.label}</span>
                  <span>{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </SectionCard>

      <div className="patient-profile-account-payment-grid">
        <SectionCard title="Digital banks" className="patient-profile-section">
          <div className="patient-profile-account-item-list">
            {!forceEmptyStates && paymentMethods.length > 0 ? (
              paymentMethods.map((item) => (
                <PatientProfilePaymentMethodItemCard
                  key={item.id}
                  item={item}
                  readOnly={useDemoData}
                  onDelete={() => handleRequestDeletePaymentMethod(item)}
                  onSetDefault={() => handleSetDefaultPaymentMethod(item)}
                  setDefaultDisabled={isSettingDefault}
                />
              ))
            ) : (
              <ProfileEmptyState
                icon={WalletIcon}
                title="No digital banks"
                description="You don't have any digital banks on record."
              />
            )}
          </div>
        </SectionCard>

        <SectionCard
          title="Credit/Debit cards"
          className="patient-profile-section"
        >
          <div className="patient-profile-account-item-list">
            {!forceEmptyStates && savedCards.length > 0 ? (
              savedCards.map((item) => (
                <PatientProfileSavedCardItemCard
                  key={item.id}
                  item={item}
                  readOnly={useDemoData}
                  onDelete={() => handleRequestDeleteSavedCard(item)}
                  onSetDefault={() => handleSetDefaultSavedCard(item)}
                  setDefaultDisabled={isSettingDefault}
                />
              ))
            ) : (
              <ProfileEmptyState
                icon={BillingIcon}
                title="No credit/debit cards"
                description="You don't have any credit/debit cards on record."
              />
            )}
          </div>
        </SectionCard>
      </div>

      <ConfirmDeleteModal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        onConfirm={() => void handleConfirmDelete()}
        title={deleteModalTitle}
        bodyText={deleteModalBody}
        confirmButtonText={isDeleting ? 'Removing...' : 'Remove'}
        confirmDisabled={isDeleting}
      />
    </div>
  )
}

export default PatientProfileAccountSummary
